/* =============================================================================
   Copy for the eCashflow Acquisitions landing page.

   Sourced from ecashflowacquisitions.com (home, deals, off-market, financing,
   buyer-snapshot) so the page says what ECFA says. Kept apart from the layout
   so the wording can be corrected without touching JSX — ECFA owns these
   words, we own the design.

   PRICING IS NOT HERE. Plans come from the platform's own tiers via
   components/OrgPlans.tsx, driven by the org record. A partner chooses which
   of our plans it offers; it does not set prices.
   ========================================================================== */

export const HERO = {
  eyebrow: "eCashflow Acquisitions",
  /** Split so the second half can take the gold gradient. */
  headlineLead: "We help acquisition entrepreneurs find, finance and",
  headlineAccent: "close the right business.",
  sub:
    "Access listed and off-market acquisition opportunities, evaluate which businesses fit " +
    "your profile, and understand the capital structure needed to pursue the right deal — " +
    "with a credible path to closing.",
  /** ECFA's own framing of what makes a deal work. */
  equation: ["The right buyer", "the right deal", "the right capital", "the right structure"],
};

export const PILLARS = [
  {
    icon: "search" as const,
    title: "Listed and off-market deal access",
    body:
      "Search listed opportunities and a substantial private off-market inventory sourced " +
      "through direct owner outreach, advisors, referral networks and private channels.",
  },
  {
    icon: "gauge" as const,
    title: "Know what you can realistically buy",
    body:
      "Understand whether a business aligns with your capital position and acquisition " +
      "criteria before you commit months to it.",
  },
  {
    icon: "handshake" as const,
    title: "Make your offers more credible",
    body:
      "See how lenders read a deal, and what offer structures are realistic, before you " +
      "submit a letter of intent.",
  },
];

export const OFF_MARKET = {
  eyebrow: "Off-market",
  title: "Private deal flow beyond the listing marketplaces",
  body:
    "This is not a feed of recycled public listings. Buyers gain visibility into directly " +
    "sourced opportunities and potential seller conversations before many businesses enter a " +
    "formal sale process.",
  stats: [
    { value: "Several thousand", label: "private acquisition opportunities" },
    { value: "Hundreds", label: "added every week" },
    { value: "High six to eight figures", label: "deal size range" },
  ],
  sectors: [
    "Essential services",
    "Construction & specialty trades",
    "Recurring B2B services",
    "Manufacturing",
    "Distribution",
    "Logistics",
    "Healthcare",
    "Professional services",
    "Ecommerce & SaaS",
  ],
};

export const ACCESS_STEPS = [
  {
    title: "Request access",
    body: "Tell us you want in. It takes a minute and there is nothing to prepare.",
  },
  {
    title: "Complete your Buyer Acquisition Snapshot",
    body:
      "Your criteria, target deal size, background, liquidity, financing readiness and " +
      "intended role. Roughly two to three minutes.",
  },
  {
    title: "Review your access options",
    body: "We come back with what fits — and an honest read on your buying range.",
  },
  {
    title: "Search deals and request introductions",
    body:
      "Browse listed and off-market opportunities, then ask for an introduction on the ones " +
      "worth a conversation.",
  },
];

export const SNAPSHOT = {
  eyebrow: "Buyer Acquisition Snapshot",
  title: "Find out what you can actually buy — before you spend six months looking",
  problem:
    "Many buyers spend months reviewing businesses without knowing whether the opportunity " +
    "fits their capital position, financing path, operating role or acquisition criteria.",
  reviewedLabel: "What we review",
  reviewed: [
    "Acquisition criteria",
    "Target deal size",
    "Professional background",
    "Operating experience",
    "Available liquidity",
    "Financing readiness",
    "Intended ownership role",
    "Current stage in the process",
  ],
  cta: "Tell us what you're looking to acquire",
  ctaNote: "Takes approximately 2–3 minutes.",
};

export const FINANCING = {
  eyebrow: "Financing",
  title: "Creative financing options to help you land your next acquisition",
  body:
    "Financing an acquisition isn't always straightforward — many businesses aren't SBA " +
    "eligible, and traditional banks decline what they don't understand. ECFA works through " +
    "a network of capital partners, whether the deal came from us or you found it yourself.",
  products: [
    { name: "SBA loans", amount: "Up to $5M", note: "Favourable terms, no prepayment penalties" },
    { name: "Business term loan", amount: "Up to $10M", note: "Long-term, no prepayment penalties" },
    { name: "Business advance", amount: "Up to $10M", note: "Cash-flow based, flexible payment terms" },
    { name: "Business line of credit", amount: "Up to $750K", note: "Revolving — pay only for what you use" },
    { name: "Acquisition lending", amount: "Bridge", note: "For businesses transitioning to an SBA loan" },
    { name: "Other structures", amount: "Varies", note: "Revenue-based, merchant advance, equipment & inventory" },
  ],
};

export const CLOSING = {
  title: "The right buyer, the right deal, the right capital.",
  body:
    "Join eCashflow Acquisitions to search listed and off-market opportunities, run the " +
    "numbers, and get matched to deals that fit your criteria.",
};

/** Shown once, in small type, at the foot of the page. ECFA's own wording. */
export const DISCLAIMER =
  "Information is based on materials provided by sellers and deal sources and has not " +
  "necessarily been independently verified. Availability, pricing, financial performance and " +
  "process requirements are subject to change. A Master NDA is required before confidential " +
  "information memorandums are released.";
